/**
 * TiptapBubbleMenu — floating selection toolbar for the Tiptap editor.
 *
 * Phase 4 of the editor rebuild. Replaces the legacy DOM-Selection-
 * based RewriteBubble with a Tiptap-aware variant that:
 *   - Shows on any non-empty selection inside the editor
 *   - Offers Bold / Italic / Underline mark toggles (mirrors Cmd+B/I/U)
 *   - Offers an "AI rewrite" affordance — preset buttons + custom
 *     instruction → POST /meetings/:id/rewrite → result preview →
 *     Apply replaces the selected range via a ProseMirror transaction.
 *
 * The internal rewrite UI (presets, custom field, "Asking Claude…",
 * result + Apply/Dismiss) mirrors the legacy RewriteBubble so users
 * see a consistent flow regardless of which editor they're in. The
 * key difference is the selection model: Tiptap owns the selection
 * via editor.state.selection (from/to positions in ProseMirror's
 * coordinate space), so Apply is `insertContentAt({from, to}, text)`
 * instead of legacy's "find-and-replace inside the bullet."
 */
import { useCallback, useRef, useState } from 'react';
import { BubbleMenu, type Editor } from '@tiptap/react';
import { NodeSelection, type EditorState } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import { useMutation } from '@tanstack/react-query';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Sparkles,
  X,
  Quote,
  User,
} from 'lucide-react';
import { api } from '../../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../../lib/noAutofill.ts';
import { classifyAiError } from '../../../lib/aiErrorMessage.ts';
import { useAiProgress } from '../../../lib/aiProgressMessage.ts';

/** When the user's selection lives entirely inside a single
 *  `bullet` node, return a handle to it so we can surface the
 *  speaker / direct-quote affordances. Returns null when the
 *  selection spans multiple bullets, lives in a different node
 *  type (action item, attendee, header), or is empty.
 *
 *  Walks the $from depth chain inside-out — bullets nest inside
 *  topics inside docs, so the bullet ancestor is always at depth
 *  ≥ 1 if it exists. The $to.pos check confirms the ENTIRE
 *  selection sits within the same bullet's range. */
function findBulletInSelection(
  state: EditorState,
): { node: PMNode; pos: number } | null {
  const { $from, $to } = state.selection;
  for (let d = $from.depth; d >= 0; d--) {
    if ($from.node(d).type.name === 'bullet') {
      const pos = $from.before(d);
      const node = $from.node(d);
      if ($to.pos > pos && $to.pos <= pos + node.nodeSize) {
        return { node, pos };
      }
      return null;
    }
  }
  return null;
}

interface Props {
  editor: Editor | null;
  meetingId: string;
}

/** Preset rewrite instructions — same set as the legacy RewriteBubble
 *  so users get the same affordances in either editor. */
const PRESETS: Array<{ label: string; instruction: string }> = [
  {
    label: 'Shorter',
    instruction: 'Make it shorter - aim for roughly half the original length.',
  },
  {
    label: 'More formal',
    instruction: 'Rewrite in a more formal, professional tone.',
  },
  {
    label: 'Expand',
    instruction:
      'Expand with more detail and context, while staying true to the facts.',
  },
  {
    label: 'Simpler',
    instruction: 'Simplify the language. Short sentences, everyday words.',
  },
];

type Mode =
  /** Default state: marks toolbar + "AI" trigger. */
  | { kind: 'marks' }
  /** Speaker input — only available when selection is in a single
   *  bullet. Sets the bullet's speaker attr on close. */
  | { kind: 'speaker'; draft: string }
  /** Preset menu: 4 quick buttons + "Custom…" + close. */
  | { kind: 'rewrite-presets' }
  /** Custom instruction input. */
  | { kind: 'rewrite-custom'; instruction: string }
  /** Streaming/awaiting Claude response. */
  | { kind: 'rewriting' }
  /** Result returned — Apply / Dismiss. */
  | { kind: 'result'; rewrittenText: string; rationale?: string };

export function TiptapBubbleMenu({ editor, meetingId }: Props) {
  const [mode, setMode] = useState<Mode>({ kind: 'marks' });
  // shouldShow runs inside Tiptap's BubbleMenuPlugin, which is
  // registered as a ProseMirror plugin. Some Tiptap versions cache
  // the original shouldShow reference, so closure-captured React
  // state can go stale. Mirroring `mode` into a ref guarantees the
  // plugin always sees the latest value on each editor update.
  const modeRef = useRef(mode);
  modeRef.current = mode;
  // Snapshot of the from/to range at the moment a rewrite was kicked
  // off. We capture this so Apply still targets the right range even
  // if the user's selection drifts while they're reading the result.
  const rangeRef = useRef<{ from: number; to: number } | null>(null);

  const rewriteMutation = useMutation({
    mutationFn: async ({
      selectedText,
      instruction,
    }: {
      selectedText: string;
      instruction: string;
    }) =>
      api.post<{ rewrittenText: string; rationale?: string; usage: unknown }>(
        `/meetings/${meetingId}/rewrite`,
        { selectedText, instruction },
      ),
    onSuccess: (data) => {
      setMode({
        kind: 'result',
        rewrittenText: data.rewrittenText,
        rationale: data.rationale,
      });
    },
    onError: () => {
      // Surface the error in the bubble; user can dismiss or retry.
      setMode({ kind: 'rewrite-presets' });
    },
  });

  /** Reset to default marks toolbar — used after Apply / Dismiss /
   *  selection change. */
  const resetToMarks = useCallback(() => {
    setMode({ kind: 'marks' });
    rangeRef.current = null;
    rewriteMutation.reset();
  }, [rewriteMutation]);

  /** Capture the editor's current selection text + range and kick
   *  off the rewrite mutation. */
  const startRewrite = useCallback(
    (instruction: string) => {
      if (!editor) return;
      const { from, to } = editor.state.selection;
      if (from === to) return;
      const selectedText = editor.state.doc.textBetween(from, to, '\n');
      if (!selectedText.trim()) return;
      rangeRef.current = { from, to };
      setMode({ kind: 'rewriting' });
      rewriteMutation.mutate({ selectedText, instruction });
    },
    [editor, rewriteMutation],
  );

  /** Replace the captured range with the rewritten text via a
   *  ProseMirror transaction, then close the bubble. */
  const applyRewrite = useCallback(
    (rewrittenText: string) => {
      if (!editor || !rangeRef.current) return;
      const { from, to } = rangeRef.current;
      editor
        .chain()
        .focus()
        .insertContentAt({ from, to }, rewrittenText)
        .run();
      resetToMarks();
    },
    [editor, resetToMarks],
  );

  if (!editor) return null;

  // Bullet-scoped affordances: when the selection is entirely
  // within a single bullet, we expose a "Speaker" pill that opens
  // a name input, plus a Quote toggle that flips the bullet's
  // isDirectQuote attr. These give the user inline access to the
  // bullet attrs that previously could only be set via Claude's
  // dialogs (SpeakerAttributionDialog, etc.).
  const bulletInSelection = findBulletInSelection(editor.state);
  const bulletAttrs = bulletInSelection
    ? (bulletInSelection.node.attrs as { speaker?: string | null; isDirectQuote?: boolean })
    : null;
  const currentSpeaker = bulletAttrs?.speaker ?? '';
  const isQuote = bulletAttrs?.isDirectQuote === true;

  const setSpeaker = (next: string) => {
    if (!bulletInSelection) return;
    const trimmed = next.trim();
    editor
      .chain()
      .focus()
      .updateAttributes('bullet', { speaker: trimmed || null })
      .run();
  };

  const toggleQuote = () => {
    if (!bulletInSelection) return;
    editor
      .chain()
      .focus()
      .updateAttributes('bullet', { isDirectQuote: !isQuote })
      .run();
  };

  // Friendly error classification — maps 429 / 422 / 400 / network
  // failures onto pilot-readable headlines + advice. See
  // aiErrorMessage.ts for the full mapping.
  const classifiedError = rewriteMutation.error
    ? classifyAiError(rewriteMutation.error)
    : null;

  // Rotating progress message so the "Asking Claude…" state isn't
  // a static 60s spinner. Returns escalating copy + an elapsed
  // counter we render alongside on long calls.
  const progress = useAiProgress(mode.kind === 'rewriting');

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ duration: 80, placement: 'top' }}
      // shouldShow keeps the bubble visible during the rewrite flow
      // even after the user moves focus into the bubble's input
      // field. Without this, the BubbleMenu plugin would auto-hide
      // the moment the editor loses DOM focus.
      shouldShow={({ editor: ed, state }) => {
        // CRITICAL: hide the bubble while a drag is in progress.
        // Otherwise the dragstart handler's NodeSelection dispatch
        // pops the bubble open over the cursor, which intercepts
        // dragover events on the way to view.dom \u2014 PM thinks the
        // drop is invalid and shows the "not allowed" cursor. The
        // bubble can also re-show after the drop because the
        // NodeSelection persists; the same check keeps it hidden
        // until the user clears the selection.
        const draggingView = ed.view as unknown as {
          dragging?: { slice: unknown; move: boolean } | null;
        };
        if (draggingView.dragging) return false;
        if (state.selection instanceof NodeSelection) return false;

        if (modeRef.current.kind !== 'marks') return true;
        const { from, to, empty } = state.selection;
        if (empty) return false;
        // Don't show for selections that span node boundaries in
        // weird ways (e.g. the user dragged across atoms). Phase 4
        // keeps this conservative \u2014 only show when there's actual
        // text content in the range.
        const text = ed.state.doc.textBetween(from, to, '\n').trim();
        return text.length > 0;
      }}
    >
      {/* Outer wrapper holds both the pine pill (marks) and the
          popover (rewrite states). flex-col so the popover stacks
          directly under the pill, mirroring the TiptapTopToolbar
          dropdown layout. */}
      <div
        role="toolbar"
        aria-label="Selection toolbar"
        className="flex flex-col items-stretch gap-1.5"
        // Don't let mousedown inside the bubble collapse the selection.
        onMouseDown={(e) => e.preventDefault()}
      >
        {/* Studio pine pill — always shown. Same shape + colors as
            ScaledPaper's zoom toolbar and the TiptapTopToolbar. */}
        <div className="mx-auto flex w-fit items-center gap-1 rounded-full bg-pine p-1 shadow-md">
          <ToolbarMarkButton
            label="Bold (Cmd+B)"
            icon={<Bold size={13} strokeWidth={2.5} />}
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
          <ToolbarMarkButton
            label="Italic (Cmd+I)"
            icon={<Italic size={13} strokeWidth={2.5} />}
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
          <ToolbarMarkButton
            label="Underline (Cmd+U)"
            icon={<UnderlineIcon size={13} strokeWidth={2.5} />}
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          />
          {/* Bullet-only affordances. Only render when the
              selection lives inside a single bullet — otherwise
              the buttons would be confusing (what bullet would
              they target?) and updateAttributes('bullet', …)
              wouldn't have an ancestor to update. */}
          {bulletInSelection && (
            <>
              <div className="mx-0.5 h-4 w-px bg-cloud/20" />
              <button
                type="button"
                onClick={() =>
                  setMode(
                    mode.kind === 'speaker'
                      ? { kind: 'marks' }
                      : { kind: 'speaker', draft: currentSpeaker },
                  )
                }
                title={
                  currentSpeaker
                    ? `Speaker: ${currentSpeaker}`
                    : 'Set speaker for this bullet'
                }
                aria-label="Set speaker for this bullet"
                className={
                  'flex h-7 items-center gap-1 rounded-full px-2 text-[11px] font-semibold transition-colors ' +
                  (mode.kind === 'speaker' || currentSpeaker
                    ? 'bg-cloud text-pine'
                    : 'text-cloud hover:bg-cloud/15')
                }
              >
                <User size={12} strokeWidth={2.5} />
                {currentSpeaker || 'Speaker'}
              </button>
              <ToolbarMarkButton
                label={isQuote ? 'Unmark direct quote' : 'Mark as direct quote'}
                icon={<Quote size={13} strokeWidth={2.5} />}
                active={isQuote}
                onClick={toggleQuote}
              />
            </>
          )}
          <div className="mx-0.5 h-4 w-px bg-cloud/20" />
          <button
            type="button"
            onClick={() =>
              setMode(
                mode.kind === 'marks' || mode.kind === 'speaker'
                  ? { kind: 'rewrite-presets' }
                  : { kind: 'marks' },
              )
            }
            title="Rewrite with AI"
            aria-label="Rewrite with AI"
            className={
              'flex h-7 items-center gap-1 rounded-full px-2 text-[11px] font-semibold transition-colors ' +
              (mode.kind !== 'marks' && mode.kind !== 'speaker'
                ? 'bg-cloud text-pine'
                : 'text-cloud hover:bg-cloud/15')
            }
          >
            <Sparkles size={12} strokeWidth={2.5} />
            Rewrite
          </button>
        </div>

        {/* Popover panel — opens beneath the pine pill when the
            user is in any mode other than `marks`. Hosts both the
            speaker input and the rewrite flow; same card chrome
            for visual consistency. */}
        {mode.kind !== 'marks' && (
          <div
            className="mx-auto w-full overflow-hidden rounded-lg border border-charcoal/15 bg-white shadow-xl"
            style={{ minWidth: 240, maxWidth: 380 }}
          >
            {mode.kind === 'speaker' && (
              <form
                className="p-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  setSpeaker(mode.draft);
                  setMode({ kind: 'marks' });
                }}
              >
                <div className="mb-1 px-1 text-[10px] font-bold uppercase tracking-[0.14em] text-pine">
                  Bullet speaker
                </div>
                <input
                  {...NO_AUTOFILL}
                  type="text"
                  autoFocus
                  value={mode.draft}
                  onChange={(e) =>
                    setMode({ kind: 'speaker', draft: e.target.value })
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setMode({ kind: 'marks' });
                    }
                  }}
                  placeholder="e.g. Alice, AC, Owner"
                  className="w-full rounded border border-charcoal/25 px-2 py-1 text-xs focus:border-pine focus:outline-none"
                />
                <div className="mt-2 flex justify-between text-[11px]">
                  {currentSpeaker ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSpeaker('');
                        setMode({ kind: 'marks' });
                      }}
                      className="text-charcoal hover:text-pine"
                    >
                      Clear
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    type="submit"
                    className="rounded-full bg-pine px-3 py-0.5 text-cloud hover:bg-pine/90"
                  >
                    Save
                  </button>
                </div>
              </form>
            )}

            {mode.kind === 'rewrite-presets' && (
              <div className="flex flex-col p-1.5 text-xs text-charcoal">
                <div className="mb-1 px-2 text-[10px] font-bold uppercase tracking-[0.14em] text-pine">
                  Rewrite selection
                </div>
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => startRewrite(p.instruction)}
                    className="rounded px-2 py-1 text-left transition-colors hover:bg-pine/10 hover:text-pine"
                  >
                    {p.label}
                  </button>
                ))}
                <div className="my-1 border-t border-charcoal/10" />
                <button
                  type="button"
                  onClick={() =>
                    setMode({ kind: 'rewrite-custom', instruction: '' })
                  }
                  className="rounded px-2 py-1 text-left transition-colors hover:bg-pine/10 hover:text-pine"
                >
                  Custom instruction…
                </button>
              </div>
            )}

            {mode.kind === 'rewrite-custom' && (
              <form
                className="p-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (mode.instruction.trim()) {
                    startRewrite(mode.instruction.trim());
                  }
                }}
              >
                <input
                  {...NO_AUTOFILL}
                  type="text"
                  autoFocus
                  value={mode.instruction}
                  onChange={(e) =>
                    setMode({
                      kind: 'rewrite-custom',
                      instruction: e.target.value,
                    })
                  }
                  placeholder="How should Claude rewrite this?"
                  className="w-full rounded border border-charcoal/25 bg-white px-2 py-1 text-xs focus:border-pine focus:outline-none"
                />
                <div className="mt-2 flex justify-between text-[11px]">
                  <button
                    type="button"
                    onClick={() => setMode({ kind: 'rewrite-presets' })}
                    className="text-charcoal hover:text-pine"
                  >
                    ← back
                  </button>
                  <button
                    type="submit"
                    disabled={!mode.instruction.trim()}
                    className="rounded-full bg-pine px-3 py-0.5 text-cloud hover:bg-pine/90 disabled:opacity-50"
                  >
                    Rewrite
                  </button>
                </div>
              </form>
            )}

            {mode.kind === 'rewriting' && (
              <div className="flex items-center gap-2 p-3 text-xs text-charcoal">
                <Sparkles size={12} className="animate-pulse text-pine shrink-0" />
                <span className="flex-1">{progress.message}</span>
                {progress.elapsedSeconds >= 5 && (
                  <span className="text-[10px] text-charcoal/50">
                    {progress.elapsedSeconds}s
                  </span>
                )}
              </div>
            )}

            {mode.kind === 'result' && (
              <div className="p-3 text-xs">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-pine">
                  Claude's rewrite
                </div>
                <div className="mb-2 rounded border border-pine/30 bg-pine/5 px-2 py-1.5 text-charcoal">
                  {mode.rewrittenText}
                </div>
                {mode.rationale && (
                  <div className="mb-2 text-[10px] italic text-charcoal">
                    {mode.rationale}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={resetToMarks}
                    className="text-charcoal hover:text-pine"
                  >
                    Dismiss
                  </button>
                  <button
                    type="button"
                    onClick={() => applyRewrite(mode.rewrittenText)}
                    className="rounded-full bg-pine px-3 py-1 text-cloud hover:bg-pine/90"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}

            {classifiedError && (
              <div
                className={
                  'border-t px-3 py-1.5 text-[11px] ' +
                  (classifiedError.kind === 'rateLimit'
                    ? 'border-amber-200 bg-amber-50 text-amber-800'
                    : 'border-red-200 bg-red-50 text-red-700')
                }
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <div className="font-semibold">{classifiedError.title}</div>
                    {classifiedError.advice && (
                      <div className="mt-0.5 opacity-80">
                        {classifiedError.advice}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => rewriteMutation.reset()}
                    className="shrink-0 opacity-60 transition-opacity hover:opacity-100"
                    aria-label="Dismiss error"
                  >
                    <X size={10} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </BubbleMenu>
  );
}

/** Compact mark-toggle button - styled to match the studio
 *  pine-pill toolbar pattern: cloud icons on pine when idle,
 *  inverted (cloud bg, pine text) when active. Same shape as
 *  the buttons in TiptapTopToolbar and ScaledPaper's zoom
 *  toolbar so the bubble feels native to the app. */
function ToolbarMarkButton({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={
        'flex h-7 w-7 items-center justify-center rounded-full transition-colors ' +
        (active
          ? 'bg-cloud text-pine'
          : 'text-cloud hover:bg-cloud/15')
      }
    >
      {icon}
    </button>
  );
}
